import React, { useState } from 'react';
import { reportsService } from '../services/api';
import './Reports.css';
import { formatPropertyName } from '../utils/strings';

const Reports = () => {
  const [reportType, setReportType] = useState('donor-summary');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: '',
    status: '',
    donorId: '',
    organizationId: ''
  });

  const reportTypes = [
    {
      id: 'donor-summary',
      name: 'Donor Summary',
      description: 'Overview of all donors with total contributions'
    },
    {
      id: 'donation-history',
      name: 'Donation History',
      description: 'Detailed donation records with date and amount filters'
    },
    {
      id: 'donor-trends',
      name: 'Donor Trends',
      description: 'Year-over-year donation trends and patterns'
    },
    {
      id: 'top-donors',
      name: 'Top Donors',
      description: 'Highest contributing donors ranked by total amount'
    },
    {
      id: 'grant-status',
      name: 'Grant Status',
      description: 'Grant applications with current status and amounts'
    },
    {
      id: 'grant-pipeline',
      name: 'Grant Pipeline',
      description: 'Pipeline of grants by status and funding amount'
    },
    {
      id: 'organization-grants',
      name: 'Organization Grants',
      description: 'Grants grouped by organization with success rates'
    },
    {
      id: 'donation-by-type',
      name: 'Donations by Type',
      description: 'Donation breakdown by type and contribution amount'
    },
    {
      id: 'monthly-revenue',
      name: 'Monthly Revenue',
      description: 'Monthly donation revenue trends and totals'
    },
    {
      id: 'donor-retention',
      name: 'Donor Retention',
      description: 'Returning donors vs new donors analysis'
    }
  ];

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setReportData(null);

    try {
      // Clean up filters - only send non-empty ones
      const activeFilters = Object.keys(filters).reduce((acc, key) => {
        if (filters[key] !== '') {
          acc[key] = filters[key];
        }
        return acc;
      }, {});

      const response = await reportsService.generate(reportType, activeFilters);
      setReportData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate report');
      console.error('Report generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!reportData) return;

    const csv = generateCSV(reportData, reportType);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportType}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const generateCSV = (data, type) => {
    if (!data || !data.summary) return '';

    let csv = `Report: ${type}\nGenerated: ${new Date().toLocaleString()}\n\n`;

    // Add summary
    if (data.summary) {
      csv += 'SUMMARY\n';
      Object.entries(data.summary).forEach(([key, value]) => {
        csv += `${key},${typeof value === 'number' ? value.toLocaleString() : value}\n`;
      });
      csv += '\n';
    }

    // Add details
    if (data.details && Array.isArray(data.details)) {
      if (data.details.length > 0) {
        csv += 'DETAILS\n';
        const headers = Object.keys(data.details[0]);
        csv += headers.join(',') + '\n';
        data.details.forEach(row => {
          const values = headers.map(h => {
            const val = row[h];
            if (typeof val === 'string' && val.includes(',')) {
              return `"${val}"`;
            }
            return val || '';
          });
          csv += values.join(',') + '\n';
        });
      }
    }

    return csv;
  };

  const renderReportContent = () => {
    if (!reportData) return null;

    try {
        return (
          <div className="report-content">
            {reportData.summary && (
              <div className="report-section">
                <h3>Summary</h3>
                <div className="summary-grid">
                  {Object.entries(reportData.summary).map(([key, value]) => {
                    // Skip rendering objects as summary items (they get expanded below)
                    if (typeof value === 'object' && value !== null) {
                      return null;
                    }
                    return (
                      <div key={key} className="summary-item">
                        <label>{formatPropertyName(key)}</label>
                        <span className="summary-value">
                          {typeof value === 'number' && value > 100
                            ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : typeof value === 'number'
                            ? value.toLocaleString()
                            : value}
                        </span>
                      </div>
                    );
                  })}
                </div>
                
                {/* Render nested objects (like by_status) as additional info */}
                <div className="summary-details">
                  {Object.entries(reportData.summary).map(([key, value]) => {
                    if (typeof value === 'object' && value !== null) {
                      return (
                        <div key={key} className="summary-breakdown">
                          <h4>{formatPropertyName(key)}</h4>
                          <div className="breakdown-items">
                            {Object.entries(value).map(([subKey, subValue]) => (
                              <div key={subKey} className="breakdown-item">
                                <span className="breakdown-label">{subKey}</span>
                                <span className="breakdown-value">{subValue}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            )}
    
            {reportData.details && Array.isArray(reportData.details) && reportData.details.length > 0 && (
              <div className="report-section">
                <h3>Details</h3>
                <div className="table-container">
                  <table className="report-table">
                    <thead>
                      <tr>
                        {Object.keys(reportData.details[0]).map(key => (
                          <th key={key}>{formatPropertyName(key)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.details.map((row, idx) => (
                        <tr key={idx}>
                          {Object.entries(row).map(([key, value]) => (
                            <td key={`${idx}-${key}`}>
                              {value}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
    
            {reportData.chart && (
              <div className="report-section">
                <h3>Chart Data</h3>
                <pre>{JSON.stringify(reportData.chart, null, 2)}</pre>
              </div>
            )}
          </div>
        );
    } catch (err) {
        console.error('Error rendering report content:', err);
        return <div className="alert alert-error">Error displaying report data.</div>;
    }
  };

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h1>📊 Reports</h1>
        <p>Generate detailed reports on donations, donors, and grants</p>
      </div>

      <div className="reports-main">
        {/* Report Type Selection - Compact Dropdown */}
        <div className="report-selector-compact">
          <div className="report-type-selector">
            <label htmlFor="reportTypeSelect">Select Report Type</label>
            <select
              id="reportTypeSelect"
              value={reportType}
              onChange={(e) => {
                setReportType(e.target.value);
                setReportData(null);
                setError('');
              }}
            >
              {reportTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
            <p className="report-description">
              {reportTypes.find(t => t.id === reportType)?.description}
            </p>
          </div>
          <h2>Filters</h2>
          <form onSubmit={handleGenerateReport}>
            <div className="filters-grid">
              <div className="filter-group">
                <label htmlFor="startDate">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="filter-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="filter-group">
                <label htmlFor="minAmount">Minimum Amount ($)</label>
                <input
                  type="number"
                  id="minAmount"
                  name="minAmount"
                  value={filters.minAmount}
                  onChange={handleFilterChange}
                  step="0.01"
                />
              </div>

              <div className="filter-group">
                <label htmlFor="maxAmount">Maximum Amount ($)</label>
                <input
                  type="number"
                  id="maxAmount"
                  name="maxAmount"
                  value={filters.maxAmount}
                  onChange={handleFilterChange}
                  step="0.01"
                />
              </div>

              <div className="filter-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">All</option>
                  <option value="applied">Applied</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="funded">Funded</option>
                  <option value="withdrawn">Withdrawn</option>
                </select>
              </div>
            </div>

            <div className="filter-actions">
              <button
                type="submit"
                className="btn-generate"
                disabled={loading}
              >
                {loading ? 'Generating...' : 'Generate Report'}
              </button>
              {reportData && (
                <button
                  type="button"
                  className="btn-export"
                  onClick={handleExportCSV}
                >
                  Export to CSV
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {/* Report Content */}
        {reportData && renderReportContent()}
      </div>
    </div>
  );
};

export default Reports;
